import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAppReviewAdminConfig,
  getAppReviewAdminSummary,
  saveAppReviewAdminConfig,
} from '../services/engagementService';

const EVENT_LABELS = {
  usage_day: ['Kullanım günü', 'Usage day'],
  meaningful_action: ['Değerli işlem', 'Meaningful action'],
  native_request_started: ['Native istek başladı', 'Native request started'],
  native_request_accepted: ['iOS isteği kabul edildi', 'iOS request accepted'],
  native_flow_completed: ['Android akışı tamamlandı', 'Android flow completed'],
  native_unavailable: ['Native akış yok', 'Native flow unavailable'],
  native_error: ['Native hata', 'Native error'],
  store_listing_opened: ['Mağaza sayfası açıldı', 'Store listing opened'],
};

const TRIGGER_LABELS = {
  task_completed: ['Görev tamamlandı', 'Task completed'],
  shopping_completed: ['Alışveriş tamamlandı', 'Shopping completed'],
  calendar_created: ['Takvim kaydı oluşturuldu', 'Calendar event created'],
  document_uploaded: ['Belge yüklendi', 'Document uploaded'],
  finance_created: ['Finans kaydı oluşturuldu', 'Finance record created'],
  settings_about: ['Hakkında butonu', 'About button'],
};

function label(map, key, tr) {
  return map[key]?.[tr ? 0 : 1] || key || '—';
}

function ReviewToggle({ checked, onChange }) {
  return <button type="button" className={`engToggle ${checked ? 'on' : ''}`} onClick={() => onChange(!checked)}><span /></button>;
}

function ReviewNumber({ value, min, max, onChange }) {
  return <input className="engInput" type="number" value={value ?? ''} min={min} max={max} onChange={(event) => onChange(Number(event.target.value || 0))} />;
}

export default function AppReviewAdminPanel({ tr = true }) {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [nextConfig, nextSummary] = await Promise.all([
        getAppReviewAdminConfig(),
        getAppReviewAdminSummary(days),
      ]);
      setConfig(nextConfig);
      setSummary(nextSummary);
      setStatus('');
    } catch (error) {
      setStatus(error?.message || (tr ? 'Değerlendirme verileri yüklenemedi.' : 'Review data could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [days, tr]);

  useEffect(() => { load(); }, [load]);
  const set = (key, value) => setConfig((current) => ({ ...current, [key]: value }));
  const save = async () => {
    try {
      setSaving(true);
      setConfig(await saveAppReviewAdminConfig(config));
      setStatus(tr ? 'Değerlendirme kuralları kaydedildi.' : 'Review rules saved.');
    } catch (error) {
      setStatus(error?.message || String(error));
    } finally {
      setSaving(false);
    }
  };

  const platformRows = useMemo(() => {
    const grouped = {};
    for (const row of summary?.byPlatform || []) {
      grouped[row.platform] ||= {};
      grouped[row.platform][row.event_type] = Number(row.count || 0);
    }
    return grouped;
  }, [summary]);

  if (loading && !config) return <div className="engPanel reviewAdminPanel engLoading">{tr ? 'Mağaza değerlendirme merkezi yükleniyor...' : 'Loading store review center...'}</div>;

  const totals = summary?.totals || {};
  const states = summary?.states || {};
  const ruleFields = [
    ['minAccountAgeHours', tr ? 'Minimum hesap yaşı / saat' : 'Minimum account age / hours', 24, 720],
    ['minDistinctDays', tr ? 'Farklı kullanım günü' : 'Distinct usage days', 2, 30],
    ['minMeaningfulScore', tr ? 'Minimum değer puanı' : 'Minimum value score', 2, 30],
    ['minDistinctModules', tr ? 'Farklı modül sayısı' : 'Distinct modules', 1, 8],
    ['cooldownDays', tr ? 'İstekler arası gün' : 'Days between requests', 30, 365],
    ['maxRequests365Days', tr ? '365 günde maksimum istek' : 'Maximum requests in 365 days', 1, 3],
    ['minAdditionalScoreAfterRequest', tr ? 'Yeni istek için ek puan' : 'Additional score for another request', 1, 30],
    ['rolloutPercent', tr ? 'Kademeli yayın / %' : 'Rollout / %', 0, 100],
  ];

  return <div className="engPanel reviewAdminPanel">
    <div className="engHero reviewAdminHero">
      <div><span>APP STORE + GOOGLE PLAY</span><h2>{tr ? 'Mağaza Değerlendirmeleri' : 'Store Review Management'}</h2><p>{tr ? 'Uygunluk kuralları, native değerlendirme akışları ve mağaza yönlendirmeleri bağımsız olarak yönetilir.' : 'Manage eligibility rules, native review flows and store routing independently.'}</p></div>
      <div className="reviewAdminHeroControls">
        <ReviewToggle checked={!!config?.enabled} onChange={(value) => set('enabled', value)} />
        <div className="reviewAdminActions">
          <select className="engInput" value={days} onChange={(event) => setDays(Number(event.target.value))}>
            <option value={7}>{tr ? '7 gün' : '7 days'}</option><option value={30}>{tr ? '30 gün' : '30 days'}</option><option value={90}>{tr ? '90 gün' : '90 days'}</option>
          </select>
          <button type="button" onClick={load}>{tr ? 'Yenile' : 'Refresh'}</button>
        </div>
      </div>
    </div>

    <div className="reviewAdminStats">
      {[
        [states.eligible, tr ? 'Şu an uygun' : 'Currently eligible'],
        [totals.requests, tr ? 'Native istek' : 'Native requests'],
        [Number(totals.accepted || 0) + Number(totals.completed || 0), tr ? 'Tamamlanan akış' : 'Completed flows'],
        [totals.store_opens, tr ? 'Mağaza açılışı' : 'Store opens'],
        [states.cooldown, tr ? 'Bekleme süresinde' : 'In cooldown'],
        [Number(totals.errors || 0) + Number(totals.unavailable || 0), tr ? 'Hata / kullanılamıyor' : 'Error / unavailable'],
      ].map(([value, title]) => <div key={title}><strong>{value ?? 0}</strong><span>{title}</span></div>)}
    </div>

    <div className="reviewAdminPlatforms">
      {['ios', 'android'].map((platform) => <div key={platform}><strong>{platform === 'ios' ? 'iOS' : 'Android'}</strong><span>{tr ? 'İstek' : 'Requests'}: {platformRows[platform]?.native_request_started || 0}</span><span>{tr ? 'Tamamlandı' : 'Completed'}: {(platformRows[platform]?.native_request_accepted || 0) + (platformRows[platform]?.native_flow_completed || 0)}</span><span>{tr ? 'Mağaza' : 'Store'}: {platformRows[platform]?.store_listing_opened || 0}</span></div>)}
    </div>

    <div className="reviewAdminRules">
      <div className="reviewAdminSwitches">
        <label><span>iOS</span><ReviewToggle checked={!!config?.iosEnabled} onChange={(value) => set('iosEnabled', value)} /></label>
        <label><span>Android</span><ReviewToggle checked={!!config?.androidEnabled} onChange={(value) => set('androidEnabled', value)} /></label>
        <label><span>{tr ? 'Aynı sürümde bir kez' : 'Once per version'}</span><ReviewToggle checked={!!config?.sameVersionOnce} onChange={(value) => set('sameVersionOnce', value)} /></label>
      </div>
      <div className="engFormGrid">{ruleFields.map(([key, title, min, max]) => <label key={key}><span>{title}</span><ReviewNumber value={config?.[key]} min={min} max={max} onChange={(value) => set(key, value)} /></label>)}</div>
      <div className="reviewAdminSave"><small>{tr ? 'Önerilen varsayılan: 72 saat, 3 gün, 4 puan, 2 modül, 90 gün ara ve yılda en fazla 2 istek.' : 'Recommended default: 72 hours, 3 days, 4 points, 2 modules, 90-day gap and at most 2 requests per year.'}</small><button type="button" onClick={save} disabled={saving}>{saving ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Kuralları kaydet' : 'Save rules')}</button></div>
    </div>

    <div className="reviewAdminTables">
      <div><h4>{tr ? 'En güçlü tetikleyiciler' : 'Top triggers'}</h4>{(summary?.byTrigger || []).slice(0, 8).map((row) => <p key={row.trigger_key}><span>{label(TRIGGER_LABELS, row.trigger_key, tr)}</span><strong>{row.count}</strong></p>)}</div>
      <div><h4>{tr ? 'Son olaylar' : 'Recent events'}</h4><div className="reviewAdminRecent">{(summary?.recent || []).slice(0, 12).map((row, index) => <p key={`${row.created_at}-${index}`}><span><b>{row.platform}</b> {label(EVENT_LABELS, row.event_type, tr)}<small>{label(TRIGGER_LABELS, row.trigger_key, tr)}</small></span><time>{new Date(row.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US')}</time></p>)}</div></div>
    </div>
    {status && <div className="engStatus">{status}</div>}
    <p className="reviewAdminNote">{tr ? 'Apple ve Google, kullanıcının gerçekten yıldız verip vermediğini uygulamaya bildirmez. “Tamamlandı”, native akışın tamamlandığını; puan verildiğini değil gösterir.' : 'Apple and Google do not disclose whether a user actually submitted a rating. “Completed” means the native flow completed, not that a rating was submitted.'}</p>
  </div>;
}
